import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';

/**
 * Хук для загрузки данных графика по частям (чанками)
 * @param {Object} params - Параметры загрузки
 * @param {string} params.apiUrl - URL API для загрузки данных
 * @param {string} params.startDate - Начальная дата диапазона (YYYY-MM-DD)
 * @param {string} params.endDate - Конечная дата диапазона (YYYY-MM-DD)
 * @param {Array<string>} params.groupIds - ID групп для фильтрации
 * @param {Array<string>} params.markers - Маркеры для загрузки
 * @param {string} params.aggregation - Агрегация: 'day', 'week', 'month'
 * @param {number} params.chunkSize - Размер чанка в днях (по умолчанию 30)
 */
export const useChunkedDataLoader = ({
  apiUrl,
  startDate,
  endDate,
  groupIds = [],
  markers = [],
  aggregation = 'day',
  chunkSize = 30, // Размер чанка в днях
}) => {
  const [data, setData] = useState([]); // Все загруженные данные
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadedChunks, setLoadedChunks] = useState(new Set()); // Какие чанки уже загружены
  const dataCache = useRef({}); // Кеш данных по маркерам
  const abortControllerRef = useRef(null);

  /**
   * Вычисляет все чанки для заданного временного диапазона
   */
  const calculateChunks = useCallback((start, end, chunkSizeDays) => {
    if (!start || !end) return [];
    
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    
    const chunks = [];
    let currentStart = new Date(startDateObj);
    
    while (currentStart < endDateObj) {
      const currentEnd = new Date(currentStart);
      currentEnd.setDate(currentEnd.getDate() + chunkSizeDays);
      
      // Не выходим за пределы конечной даты
      if (currentEnd > endDateObj) {
        currentEnd.setTime(endDateObj.getTime());
      }
      
      chunks.push({
        startDate: currentStart.toISOString().split('T')[0],
        endDate: currentEnd.toISOString().split('T')[0],
        key: `${currentStart.toISOString().split('T')[0]}_${currentEnd.toISOString().split('T')[0]}`,
      });
      
      currentStart = new Date(currentEnd);
      currentStart.setDate(currentStart.getDate() + 1); // Следующий день после конца текущего чанка
    }
    
    return chunks;
  }, []);

  /**
   * Загружает данные для конкретного чанка
   */
  const loadChunk = useCallback(async (chunk) => {
    if (!apiUrl || !markers.length || !groupIds.length) return null;

    const params = {
      group_ids: groupIds.join(','),
      start_date: chunk.startDate,
      end_date: chunk.endDate,
      markers: markers.join(','),
      period: aggregation,
    };

    try {
      const response = await axios.get(apiUrl, { 
        params,
        signal: abortControllerRef.current?.signal 
      });
      return response.data;
    } catch (error) {
      if (error.name === 'CanceledError') {
        console.log('Запрос отменен');
        return null;
      }
      throw error;
    }
  }, [apiUrl, groupIds, markers, aggregation]);

  /**
   * Объединяет данные из нескольких чанков
   */
  const mergeChunkData = useCallback((existingData, newChunkData) => {
    if (!newChunkData || !Array.isArray(newChunkData)) return existingData;

    const mergedMap = {};

    // Добавляем существующие данные в карту
    existingData.forEach(markerData => {
      mergedMap[markerData.marker] = {
        marker: markerData.marker,
        data: [...markerData.data],
      };
    });

    // Объединяем с новыми данными
    newChunkData.forEach(markerData => {
      if (!mergedMap[markerData.marker]) {
        mergedMap[markerData.marker] = {
          marker: markerData.marker,
          data: [],
        };
      }

      // Добавляем новые точки данных
      const existingDates = new Set(
        mergedMap[markerData.marker].data.map(d => d.date)
      );

      markerData.data.forEach(point => {
        if (!existingDates.has(point.date)) {
          mergedMap[markerData.marker].data.push(point);
        }
      });

      // Сортируем данные по дате
      mergedMap[markerData.marker].data.sort((a, b) => 
        a.date.localeCompare(b.date)
      );
    });

    return Object.values(mergedMap);
  }, []);

  /**
   * Загружает данные для указанных чанков
   */
  const loadChunks = useCallback(async (chunksToLoad) => {
    if (!chunksToLoad || chunksToLoad.length === 0) return;

    // Фильтруем уже загруженные чанки
    const newChunks = chunksToLoad.filter(chunk => !loadedChunks.has(chunk.key));
    
    if (newChunks.length === 0) {
      console.log('Все необходимые чанки уже загружены');
      return;
    }

    console.log(`Загрузка ${newChunks.length} новых чанков:`, newChunks);

    setLoading(true);
    setError(null);

    // Отменяем предыдущий запрос, если он еще выполняется
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      // Загружаем все чанки параллельно
      const chunkPromises = newChunks.map(chunk => loadChunk(chunk));
      const chunkResults = await Promise.all(chunkPromises);

      // Объединяем результаты всех чанков
      let mergedData = data;
      chunkResults.forEach(chunkData => {
        if (chunkData) {
          mergedData = mergeChunkData(mergedData, chunkData);
        }
      });

      setData(mergedData);

      // Обновляем список загруженных чанков
      setLoadedChunks(prev => {
        const newSet = new Set(prev);
        newChunks.forEach(chunk => newSet.add(chunk.key));
        return newSet;
      });

    } catch (err) {
      if (err.name !== 'CanceledError') {
        console.error('Ошибка при загрузке чанков:', err);
        setError(err.message || 'Ошибка загрузки данных');
      }
    } finally {
      setLoading(false);
    }
  }, [data, loadedChunks, loadChunk, mergeChunkData]);

  /**
   * Определяет, какие чанки нужно загрузить для заданного окна данных
   */
  const getChunksForWindow = useCallback((windowStart, windowEnd, allChunks) => {
    // Преобразуем индексы окна в даты
    if (!startDate || !endDate || !allChunks.length) return [];

    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);
    const totalDays = Math.ceil((endDateObj - startDateObj) / (1000 * 60 * 60 * 24));

    if (totalDays === 0) return allChunks.slice(0, 1);

    // Вычисляем даты для окна
    const windowStartDate = new Date(startDateObj);
    windowStartDate.setDate(windowStartDate.getDate() + Math.floor((windowStart / totalDays) * totalDays));

    const windowEndDate = new Date(startDateObj);
    windowEndDate.setDate(windowEndDate.getDate() + Math.ceil((windowEnd / totalDays) * totalDays));

    // Находим чанки, которые пересекаются с окном
    const relevantChunks = allChunks.filter(chunk => {
      const chunkStart = new Date(chunk.startDate);
      const chunkEnd = new Date(chunk.endDate);
      return chunkStart <= windowEndDate && chunkEnd >= windowStartDate;
    });

    // Добавляем буферные чанки (один до и один после для предзагрузки)
    const firstChunkIndex = allChunks.findIndex(c => c.key === relevantChunks[0]?.key);
    const lastChunkIndex = allChunks.findIndex(c => c.key === relevantChunks[relevantChunks.length - 1]?.key);

    const chunksWithBuffer = [];
    if (firstChunkIndex > 0) {
      chunksWithBuffer.push(allChunks[firstChunkIndex - 1]);
    }
    chunksWithBuffer.push(...relevantChunks);
    if (lastChunkIndex < allChunks.length - 1 && lastChunkIndex >= 0) {
      chunksWithBuffer.push(allChunks[lastChunkIndex + 1]);
    }

    return chunksWithBuffer;
  }, [startDate, endDate]);

  /**
   * Загружает данные для заданного окна
   */
  const loadDataForWindow = useCallback((windowStart, windowEnd) => {
    const allChunks = calculateChunks(startDate, endDate, chunkSize);
    const chunksToLoad = getChunksForWindow(windowStart, windowEnd, allChunks);
    loadChunks(chunksToLoad);
  }, [startDate, endDate, chunkSize, calculateChunks, getChunksForWindow, loadChunks]);

  /**
   * Сброс всех данных и состояния
   */
  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setData([]);
    setLoadedChunks(new Set());
    dataCache.current = {};
    setError(null);
    setLoading(false);
  }, []);

  /**
   * Начальная загрузка первого чанка
   */
  const loadInitialData = useCallback(() => {
    const allChunks = calculateChunks(startDate, endDate, chunkSize);
    if (allChunks.length > 0) {
      // Загружаем первые несколько чанков для начального отображения
      const initialChunks = allChunks.slice(0, Math.min(3, allChunks.length));
      loadChunks(initialChunks);
    }
  }, [startDate, endDate, chunkSize, calculateChunks, loadChunks]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    data,
    loading,
    error,
    loadDataForWindow,
    loadInitialData,
    reset,
    loadedChunksCount: loadedChunks.size,
    totalChunksCount: calculateChunks(startDate, endDate, chunkSize).length,
  };
};

